import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon, CircleCheckIcon } from '@persona-id/design-tokens/icons';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { useComputedProperty } from 'dashboard/components/GridLayoutRender/computed-properties/useComputedProperty';
import { ComputedProperty } from 'dashboard/lib/case-layouts/computed-properties';
import {
  InsightState,
  InsightSummaryIcons,
  ModuleInsightsSummary,
} from 'dashboard/modules/InsightsSummary/model';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';

const InsightsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing[4]};
`;

const InsightSummaryIconMap: Record<InsightSummaryIcons, ReactNode> = {
  [InsightSummaryIcons.CHECK]: <CircleCheckIcon />,
  [InsightSummaryIcons.ALERT]: <CircleAlertIcon />,
};

export interface InsightsSummaryProps {
  module: ModuleInsightsSummary;
  runCodeWithContext: (code: string) => Promise<unknown>;
  isPreview?: boolean;
}

const InsightsSummary = ({
  module,
  runCodeWithContext,
  isPreview = false,
}: InsightsSummaryProps) => {
  const { insights, label } = module;

  return (
    <Card>
      <Card.Body>
        <Card.Title>{label}</Card.Title>
        <InsightsContainer>
          {insights.map((insight) => {
            const { label, states } = insight;

            return states.map((state, i) => {
              return (
                <InsightStateDisplay
                  key={i}
                  label={label}
                  state={state}
                  isPreview={isPreview}
                  runCodeWithContext={runCodeWithContext}
                />
              );
            });
          })}
        </InsightsContainer>
      </Card.Body>
    </Card>
  );
};

const InsightDisplay = styled.div`
  display: flex;
  column-gap: ${Spacing[1]};
`;

interface InsightStateProps {
  label: string;
  state: InsightState;
  isPreview: boolean;
  runCodeWithContext: (code: string) => Promise<unknown>;
}

const InsightStateDisplay = ({
  label,
  state,
  isPreview,
  runCodeWithContext,
}: InsightStateProps) => {
  const { icon, color, 'display-value': displayValue, condition } = state;
  const [shouldShow] = useComputedProperty<boolean>(runCodeWithContext, condition, false);

  // in preview mode we want to show all insights regardless of evaluated code
  if (shouldShow === false && isPreview === false) {
    return null;
  }

  return (
    <div>
      <Paragraph size='sm'>{label}</Paragraph>
      <InsightDisplay>
        <div style={{ color }}>{InsightSummaryIconMap[icon]}</div>
        <Paragraph color='primary' size='sm' style={{ fontWeight: 500 }}>
          <ComputedPropertyDisplay
            computedProperty={displayValue}
            runCodeWithContext={runCodeWithContext}
          />
        </Paragraph>
      </InsightDisplay>
    </div>
  );
};

interface ComputedPropertyProps {
  computedProperty: ComputedProperty;
  runCodeWithContext: (code: string) => Promise<unknown>;
}

const ComputedPropertyDisplay = ({
  computedProperty,
  runCodeWithContext,
}: ComputedPropertyProps) => {
  const [value] = useComputedProperty<string>(runCodeWithContext, computedProperty);

  return <span>{value}</span>;
};

export default InsightsSummary;
